import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product } from '@/data/products';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProductContextType {
  products: Product[];
  categories: string[];
  addProduct: (product: Omit<Product, 'id'>) => Promise<void>;
  updateProduct: (id: string, product: Partial<Omit<Product, 'id'>>) => Promise<void>;
  removeProduct: (id: string) => Promise<void>;
  addCategory: (category: string) => Promise<void>;
  removeCategory: (category: string) => Promise<void>;
  isLoading: boolean;
  getSetting: (key: string) => Promise<string | null>;
  saveSetting: (key: string, value: string) => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export function ProductProvider({ children }: { children: React.ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
    
    const catNames = ["Todos", ...data.map(c => c.name)];
    setCategories(catNames);
    return data;
  };

  const fetchProducts = async () => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching products:', error);
      return;
    }
    
    const formattedProducts: Product[] = data.map(p => ({
      id: p.id,
      name: p.name,
      description: p.description || '',
      price: Number(p.price),
      category: p.category || '',
      category_id: p.category_id || undefined,
      images: p.images || []
    }));
    
    setProducts(formattedProducts);
  };

  useEffect(() => {
    const init = async () => {
      await fetchCategories();
      await fetchProducts();
      setIsLoading(false);
    };
    init();
  }, []);

  const addProduct = async (product: Omit<Product, 'id'>) => {
    const { data: categoryData } = await supabase
      .from('categories')
      .select('id')
      .eq('name', product.category)
      .single();

    const { error } = await supabase
      .from('products')
      .insert({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        category_id: categoryData?.id,
        images: product.images
      });

    if (error) {
      toast.error("Erro ao adicionar produto");
      console.error(error);
      return;
    }

    toast.success("Produto adicionado com sucesso!");
    fetchProducts();
  };

  const updateProduct = async (id: string, product: Partial<Omit<Product, 'id'>>) => {
    let category_id = undefined;
    if (product.category) {
      const { data: categoryData } = await supabase
        .from('categories')
        .select('id')
        .eq('name', product.category)
        .single();
      category_id = categoryData?.id;
    }

    const { error } = await supabase
      .from('products')
      .update({
        ...product,
        category_id: category_id
      })
      .eq('id', id);

    if (error) {
      toast.error("Erro ao atualizar produto");
      console.error(error);
      return;
    }

    toast.success("Produto atualizado com sucesso!");
    fetchProducts();
  };

  const removeProduct = async (id: string) => {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error("Erro ao remover produto");
      return;
    }

    toast.success("Produto removido!");
    fetchProducts();
  };

  const addCategory = async (name: string) => {
    const { error } = await supabase
      .from('categories')
      .insert({ name });

    if (error) {
      toast.error("Erro ao adicionar categoria");
      return;
    }

    toast.success("Categoria adicionada!");
    fetchCategories();
  };

  const removeCategory = async (name: string) => {
    if (name === "Todos") return;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('name', name);

      if (error) {
        console.error("Erro detalhado ao remover categoria:", error);
        toast.error(`Erro ao remover categoria: ${error.message}`);
        return;
      }

      toast.success("Categoria removida!");
      await fetchCategories();
    } catch (err) {
      console.error("Erro inesperado ao remover categoria:", err);
      toast.error("Erro inesperado ao remover categoria");
    }
  };

  const getSetting = async (key: string) => {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', key)
      .single();
    
    if (error) {
      if (error.code !== 'PGRST116') { // Not found is fine
        console.error('Error fetching setting:', error);
      }
      return null;
    }
    return data?.value;
  };

  const saveSetting = async (key: string, value: string) => {
    const { error } = await supabase
      .from('settings')
      .upsert({ key, value }, { onConflict: 'key' });

    if (error) {
      toast.error(`Erro ao salvar configuração: ${key}`);
      console.error(error);
      return;
    }

    toast.success("Configuração salva!");
  };

  return (
    <ProductContext.Provider value={{ 
      products, 
      categories, 
      addProduct, 
      updateProduct,
      removeProduct, 
      addCategory, 
      removeCategory,
      isLoading,
      getSetting,
      saveSetting
    }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
}
